import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Phone, Mail, Users2, Calendar, Plus, Check, Sparkles, Copy, ExternalLink, GraduationCap, Building2, Pencil } from "lucide-react";
import { recruiterProfile, addRecruiterActivity, addRecruiterFollowup, doneFollowup, draftRecruiterInvite, alumniOutreach } from "@/lib/campusApi";

/* One company, the whole relationship: seasons and offers, a timeline the
   team writes to, follow-ups that surface on the Recruiters page, alumni who
   work there, and an invite drafted from the college's real numbers. */

const HEALTH = {
  returning: { cls: "bg-emerald-100 text-emerald-800", label: "Returning this season" },
  active: { cls: "bg-emerald-50 text-emerald-700", label: "Active this season" },
  at_risk: { cls: "bg-red-100 text-red-700", label: "At risk: hired before, no drive this season" },
  lapsed: { cls: "bg-stone-200 text-stone-600", label: "Lapsed" },
  prospect: { cls: "bg-amber-100 text-amber-800", label: "Prospect" },
};
const KINDS = ["note", "call", "email", "meeting", "visit", "ppt"];

export default function RecruiterDrawer({ recruiter, onClose, onEdit, onChanged }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [kind, setKind] = useState("note");
  const [fuAt, setFuAt] = useState("");
  const [fuNote, setFuNote] = useState("");
  const [busy, setBusy] = useState("");
  const [invite, setInvite] = useState(recruiter?.invite_draft || null);
  const [intro, setIntro] = useState(null);
  const load = () => recruiterProfile(recruiter.recruiter_id).then(({ data }) => { setP(data); if (data.invite_draft) setInvite(data.invite_draft); }).catch((e) => setErr(e?.response?.data?.detail || "Could not load this company."));
  useEffect(() => { load(); }, [recruiter.recruiter_id]); // eslint-disable-line

  const logActivity = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy("act");
    try { await addRecruiterActivity(recruiter.recruiter_id, { kind, note }); setNote(""); load(); onChanged && onChanged(); }
    catch (_) { toast.error("Could not save."); } finally { setBusy(""); }
  };
  const addFollowup = async (e) => {
    e.preventDefault();
    if (!fuAt) return;
    setBusy("fu");
    try { await addRecruiterFollowup(recruiter.recruiter_id, { at: fuAt, note: fuNote }); setFuAt(""); setFuNote(""); load(); onChanged && onChanged(); toast.success("Follow-up set."); }
    catch (_) { toast.error("Could not set the follow-up."); } finally { setBusy(""); }
  };
  const finish = async (f) => { try { await doneFollowup(f.followup_id); load(); onChanged && onChanged(); } catch (_) {} };
  const draft = async () => {
    setBusy("invite");
    try { const { data } = await draftRecruiterInvite(recruiter.recruiter_id, {}); setInvite(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the invite."); } finally { setBusy(""); }
  };
  const warmIntro = async (a) => {
    setBusy("intro" + a.alumni_id);
    try { const { data } = await alumniOutreach(a.alumni_id, "recruit"); setIntro({ ...data, name: a.name }); }
    catch (_) { toast.error("Could not draft the message."); } finally { setBusy(""); }
  };
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied."));
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl" data-testid="recruiter-drawer">
        <div className="sticky top-0 bg-[#FDFBF7] border-b border-stone-200 px-7 py-5 flex items-start justify-between z-10">
          <div>
            <h2 className="font-display text-3xl">{recruiter.company_name}</h2>
            <p className="text-sm text-stone-500 mt-0.5 flex flex-wrap items-center gap-x-3">
              {recruiter.contact_name && <span>{recruiter.contact_name}</span>}
              {recruiter.contact_email && <a href={`mailto:${recruiter.contact_email}`} className="inline-flex items-center gap-1 hover:text-stone-900"><Mail size={12} />{recruiter.contact_email}</a>}
              {recruiter.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{recruiter.contact_phone}</span>}
              {recruiter.owner && <span>· owner {recruiter.owner}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900"><Pencil size={12} /> Edit</button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
          </div>
        </div>

        {err ? <p className="p-7 text-red-700">{err}</p> : !p ? <p className="p-7 text-stone-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p> : (
          <div className="px-7 py-6 space-y-8">
            {/* Health + next action */}
            <section className="rounded-3xl bg-stone-900 text-stone-50 p-5 wsh-grain relative overflow-hidden">
              <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(360px 200px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
              <div className="relative">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${HEALTH[p.health]?.cls || HEALTH.prospect.cls}`}>{HEALTH[p.health]?.label || p.health}</span>
                <p className="mt-3 text-sm text-stone-200"><span className="text-amber-300 font-medium">Next:</span> {p.next_action.label}</p>
                <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                  {[["Seasons", p.seasons.length], ["Drives", p.totals.drives], ["Offers", p.totals.offers], ["Median pkg", p.totals.median_lpa != null ? `₹${p.totals.median_lpa}L` : "—"]].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-white/10 px-2 py-2"><p className="font-display text-xl leading-none">{v}</p><p className="text-[10px] text-stone-300 mt-1">{l}</p></div>
                  ))}
                </div>
              </div>
            </section>

            {/* Invite */}
            <section>
              <div className="flex items-center justify-between">
                <span className="overline">Invite to recruit</span>
                <button onClick={draft} disabled={busy === "invite"} data-testid="rec-draft-invite" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] disabled:opacity-60">
                  {busy === "invite" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {invite ? "Redraft" : "Draft from our numbers"}
                </button>
              </div>
              {invite ? (
                <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-4">
                  <p className="text-sm font-medium">{invite.subject}</p>
                  <pre className="mt-2 text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{invite.body}</pre>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => copy(`${invite.subject}\n\n${invite.body}`)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900"><Copy size={12} /> Copy</button>
                    {(invite.to || recruiter.contact_email) && <a href={`mailto:${invite.to || recruiter.contact_email}?subject=${encodeURIComponent(invite.subject)}&body=${encodeURIComponent(invite.body)}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900"><Mail size={12} /> Open in mail</a>}
                  </div>
                </div>
              ) : <p className="mt-2 text-xs text-stone-500">Writes a short invite using this season's readiness, verified tests, and your history with this company. Nothing invented.</p>}
            </section>

            {/* Alumni */}
            {p.alumni.length > 0 && (
              <section>
                <span className="overline inline-flex items-center gap-1.5"><GraduationCap size={13} /> Alumni at {recruiter.company_name}</span>
                <ul className="mt-3 divide-y divide-stone-100 rounded-2xl border border-stone-200 bg-white">
                  {p.alumni.map((a) => (
                    <li key={a.alumni_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                      <div className="min-w-0"><p className="font-medium truncate">{a.name} <span className="text-stone-400 font-normal">· {a.batch_year || "batch ?"}{a.branch ? ` · ${a.branch}` : ""}</span></p><p className="text-xs text-stone-500">{a.role || (a.current ? "works there" : "worked there")}{!a.current && " (past)"}</p></div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg text-stone-400 hover:text-stone-900"><ExternalLink size={13} /></a>}
                        <button onClick={() => warmIntro(a)} disabled={busy === "intro" + a.alumni_id} className="text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900">{busy === "intro" + a.alumni_id ? "…" : "Warm intro"}</button>
                      </div>
                    </li>
                  ))}
                </ul>
                {intro && (
                  <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-xs text-amber-800 mb-1">Message to {intro.name}</p>
                    <p className="text-sm font-medium">{intro.subject}</p>
                    <pre className="mt-1 text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed">{intro.body}</pre>
                    <button onClick={() => copy(`${intro.subject}\n\n${intro.body}`)} className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900"><Copy size={12} /> Copy</button>
                  </div>
                )}
              </section>
            )}

            {/* History */}
            <section>
              <span className="overline inline-flex items-center gap-1.5"><Building2 size={13} /> Drives here</span>
              {p.history.length === 0 ? <p className="mt-2 text-sm text-stone-500">No drives yet.</p> : (
                <table className="mt-3 w-full text-sm">
                  <thead><tr className="text-[10px] font-mono uppercase tracking-widest text-stone-400 border-b border-stone-200">{["Season", "Role", "Package", "Applied", "Shortlisted", "Offers", "Forecast"].map((h) => <th key={h} className="text-left py-2 pr-3 font-normal">{h}</th>)}</tr></thead>
                  <tbody>{p.history.map((h) => (
                    <tr key={h.drive_id} className="border-b border-stone-100">
                      <td className="py-2 pr-3 font-mono text-xs">{h.season}</td><td className="py-2 pr-3">{h.role}<span className="block text-xs text-stone-400">{fmt(h.when)} · {h.status}</span></td>
                      <td className="py-2 pr-3">{h.ctc_lpa ? `₹${h.ctc_lpa}L` : h.ctc || "—"}</td><td className="py-2 pr-3 font-mono">{h.applicants}</td><td className="py-2 pr-3 font-mono">{h.shortlisted}</td>
                      <td className="py-2 pr-3 font-mono text-emerald-700">{h.offers}</td><td className="py-2 pr-3 font-mono text-stone-500">{h.forecast != null ? `~${h.forecast}` : "—"}</td>
                    </tr>))}</tbody>
                </table>
              )}
            </section>

            {/* Follow-ups */}
            <section>
              <span className="overline inline-flex items-center gap-1.5"><Calendar size={13} /> Follow-ups</span>
              {p.followups.length > 0 && (
                <ul className="mt-3 space-y-2">{p.followups.map((f) => (
                  <li key={f.followup_id} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm ${f.at <= new Date().toISOString() ? "border-red-200 bg-red-50" : "border-stone-200 bg-white"}`}>
                    <span>{fmt(f.at)} · {f.note}</span>
                    <button onClick={() => finish(f)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-stone-300 hover:border-emerald-600 hover:text-emerald-700"><Check size={12} /> Done</button>
                  </li>))}</ul>
              )}
              <form onSubmit={addFollowup} className="mt-3 flex flex-wrap gap-2">
                <input type="date" value={fuAt} onChange={(e) => setFuAt(e.target.value)} className="cm-inp w-40" data-testid="rec-fu-date" />
                <input value={fuNote} onChange={(e) => setFuNote(e.target.value)} placeholder="Call about the October slot" className="cm-inp flex-1 min-w-[180px]" />
                <button type="submit" disabled={busy === "fu" || !fuAt} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-stone-900 text-stone-50 disabled:opacity-50"><Plus size={14} /> Set</button>
              </form>
            </section>

            {/* Timeline */}
            <section>
              <span className="overline inline-flex items-center gap-1.5"><Users2 size={13} /> Timeline</span>
              <form onSubmit={logActivity} className="mt-3 flex flex-wrap gap-2">
                <select value={kind} onChange={(e) => setKind(e.target.value)} className="cm-inp w-28 capitalize">{KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Spoke to HR; they want a PPT in the first week of November" className="cm-inp flex-1 min-w-[200px]" data-testid="rec-activity-note" />
                <button type="submit" disabled={busy === "act" || !note.trim()} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-stone-900 text-stone-50 disabled:opacity-50"><Plus size={14} /> Log</button>
              </form>
              {p.activities.length === 0 ? <p className="mt-3 text-sm text-stone-500">Nothing logged yet. Every call, mail and visit here becomes next season's memory.</p> : (
                <ol className="mt-4 space-y-3 border-l border-stone-200 pl-4">{p.activities.map((a) => (
                  <li key={a.activity_id} className="text-sm relative"><span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-stone-300" />
                    <p className="text-xs text-stone-400 capitalize">{a.kind} · {fmt(a.at)}{a.by_name ? ` · ${a.by_name}` : ""}</p>
                    <p className="text-stone-800">{a.note}</p>
                  </li>))}</ol>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
